import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Grid3x3, Loader2, Upload, X } from "lucide-react";
import { api } from "../../api";
import { ApiError } from "../../api/client";
import { qk } from "../../hooks/queries";
import {
  BEHAVIOR_DIMS,
  classCode,
  classIndexFromCats,
  classLabel,
} from "../../lib/behaviorZones";
import { polygonAreaM2 } from "../../lib/samplingDesign";

// "Use existing samples" path for Behavior-Classified Zones: upload a zone file and MANUALLY
// classify each zone into the 27 behavior categories (Moisture / Productivity / Fertility), then
// Z2-save.
function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : "Something went wrong.";
}
function parseZones(text: string): GeoJSON.Feature[] {
  const g = JSON.parse(text) as { type?: string; features?: GeoJSON.Feature[] };
  const feats =
    g.type === "FeatureCollection"
      ? (g.features ?? [])
      : g.type === "Feature"
        ? [g as unknown as GeoJSON.Feature]
        : [];
  return feats.filter(
    (f) =>
      f?.geometry &&
      (f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon"),
  );
}
function zoneName(f: GeoJSON.Feature, i: number): string {
  const p = (f.properties ?? {}) as Record<string, unknown>;
  const n = p.name ?? p.zone ?? p.Zone ?? p.ZONE ?? p.id;
  return n != null ? String(n) : `Zone ${i + 1}`;
}

export function ClassifyZonesModal({
  fieldId,
  onClose,
}: {
  fieldId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [zones, setZones] = useState<GeoJSON.Feature[] | null>(null);
  // per-zone category picks [moisture, productivity, fertility], default Mid/Mid/Mid
  const [picks, setPicks] = useState<[number, number, number][]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const areas = useMemo(
    () =>
      (zones ?? []).map(
        (f) =>
          Math.round(
            (polygonAreaM2(
              f.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon,
            ) /
              4046.86) *
              100,
          ) / 100,
      ),
    [zones],
  );

  async function onFile(file: File) {
    setErr(null);
    try {
      const feats = parseZones(await file.text());
      if (!feats.length) {
        setErr(
          "No polygon zones found in that file. Upload a GeoJSON of zone polygons (FeatureCollection).",
        );
        return;
      }
      setZones(feats);
      setPicks(feats.map(() => [1, 1, 1]));
    } catch {
      setErr(
        "Could not read that file — it needs to be GeoJSON (a FeatureCollection of zone polygons).",
      );
    }
  }

  function setPick(zi: number, dim: number, cat: number) {
    setPicks((prev) =>
      prev.map((p, i) =>
        i === zi
          ? dim === 0
            ? [cat, p[1], p[2]]
            : dim === 1
              ? [p[0], cat, p[2]]
              : [p[0], p[1], cat]
          : p,
      ),
    );
  }

  async function save() {
    if (!zones) return;
    setBusy(true);
    setErr(null);
    try {
      const fc: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: zones.map((f, i) => {
          const idx = classIndexFromCats(picks[i][0], picks[i][1], picks[i][2]);
          return {
            type: "Feature",
            geometry: f.geometry,
            properties: {
              zone: idx,
              code: classCode(idx),
              label: classLabel(idx),
              area_acres: areas[i],
            },
          };
        }),
      };
      await api.saveZones(fieldId, fc);
      void qc.invalidateQueries({ queryKey: qk.layers(fieldId) });
      void qc.invalidateQueries({ queryKey: qk.fields });
      onClose();
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <div className="w-80 max-w-[calc(100vw-2rem)]">
      <div className="flex items-center justify-between border-b border-line px-3 py-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
          <Grid3x3 size={15} className="text-muted" /> Classify zones (behavior)
        </h2>
        <button
          onClick={onClose}
          className="rounded p-1 hover:bg-hover"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
      <div className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto p-3 text-[12px]">
        {!zones ? (
          <>
            <p className="text-muted">
              Upload a zone file (GeoJSON), then classify each zone into a
              behavior category (Moisture · Productivity · Fertility).
            </p>
            <label className="flex cursor-pointer items-center gap-1.5 self-start rounded bg-accent px-3 py-1.5 text-canvas hover:bg-accent-hover">
              <Upload size={13} /> Choose GeoJSON zone file
              <input
                type="file"
                accept=".geojson,.json,application/geo+json,application/json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFile(f);
                }}
              />
            </label>
          </>
        ) : (
          <>
            <p className="text-muted">
              {zones.length} {zones.length === 1 ? "zone" : "zones"} — pick each
              one’s category:
            </p>
            {zones.map((f, i) => (
              <div key={i} className="rounded border border-line p-2">
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-medium text-fg">{zoneName(f, i)}</span>
                  <span className="text-[10px] text-faint">
                    {areas[i]} ac ·{" "}
                    {classCode(
                      classIndexFromCats(picks[i][0], picks[i][1], picks[i][2]),
                    )}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {BEHAVIOR_DIMS.map((dim, di) => (
                    <label
                      key={di}
                      className="flex items-center gap-1 text-[11px] text-muted"
                      title={dim.name}
                    >
                      <span className="text-faint">
                        {dim.name.split(" ")[0]}:
                      </span>
                      <select
                        className="rounded border border-line bg-surface px-1 py-0.5 text-fg"
                        value={picks[i][di]}
                        onChange={(e) => setPick(i, di, Number(e.target.value))}
                        aria-label={`${zoneName(f, i)} ${dim.name}`}
                      >
                        {dim.names.map((nm, ci) => (
                          <option key={ci} value={ci}>
                            {nm}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
        {err && <div className="text-[11px] text-red-400">{err}</div>}
        {zones && (
          <div className="flex items-center gap-2 pt-1">
            <button
              className="rounded bg-accent px-3 py-1.5 text-[11px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
              disabled={busy}
              onClick={() => void save()}
            >
              {busy ? (
                <Loader2 size={12} className="inline animate-spin" />
              ) : null}{" "}
              Save {zones.length} classified{" "}
              {zones.length === 1 ? "zone" : "zones"}
            </button>
            <button
              className="rounded px-2 py-1.5 text-[11px] text-muted hover:bg-hover disabled:opacity-50"
              disabled={busy}
              onClick={() => {
                setZones(null);
                setErr(null);
              }}
            >
              Use a different file
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
